import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { IconifyIconComponent } from '../../../../shared/components/iconify-icon/iconify-icon.component';
import {
  AuthenticationService,
  User,
} from '../../../../shared/services/auth/authentication.service';
import { DatePipe, NgClass } from '@angular/common';
import { ApiService } from '../../../../core/services/api.service';
import {
  ApiResponse,
  TotpSetupResponse,
} from '../../../../core/models/api-response.model';
import { AlertifyService } from '../../../../shared/services/alertify/alertify.service';
import { finalize } from 'rxjs';

declare var QRCode: any;

@Component({
  selector: 'app-my-profile-modal',
  standalone: true,
  imports: [ModalComponent, IconifyIconComponent, DatePipe, NgClass],
  templateUrl: './my-profile-modal.component.html',
  styleUrl: './my-profile-modal.component.scss',
})
export class MyProfileModalComponent implements OnInit {
  @Output() close: EventEmitter<boolean> = new EventEmitter();

  qrCodeSignal: Signal<ElementRef<HTMLElement> | undefined> =
    viewChild<ElementRef<HTMLElement>>('qrCode');

  isActive: WritableSignal<boolean> = signal(false);
  provisioningUri: WritableSignal<string | null> = signal(null);

  mfaPhase: 'ENABLED' | 'DISABLED' | 'SETUP_PENDING' | 'PROCESSING' | null =
    null;
  manualEntryKey: string | null = null;
  isCodeInputValid: boolean = false;
  totpCode: string | null = null;
  isCopied: boolean = false;

  currentUser: Partial<User> | null = null;

  constructor(
    private readonly apiService: ApiService,
    private readonly alertifyService: AlertifyService,
    private readonly authenticationService: AuthenticationService,
  ) {
    effect((onCleanup) => {
      const element = this.qrCodeSignal()?.nativeElement;
      const uri = this.provisioningUri();

      if (!element || !uri) return;

      const qrcode = new QRCode(element, {
        text: uri,
        width: 200,
        height: 200,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H, // High error correction
      });

      onCleanup(() => {
        qrcode.clear();
        element.innerHTML = '';
        // qrcode.makeCode(value);
      });
    });
  }

  get requiresAction(): boolean {
    return !!this.currentUser?.mustChangePassword;
  }

  get requiredActionMessage(): string {
    if (this.currentUser?.mustChangePassword)
      return 'Account requires a password change';
    return '';
  }

  ngOnInit(): void {
    this.authenticationService.user$.subscribe({
      next: (value) => {
        this.currentUser = value;
        this.isActive.set(!!this.currentUser?.totpEnabled);

        if (this.currentUser?.totpEnabled) this.mfaPhase = 'ENABLED';
        else this.mfaPhase = 'DISABLED';
      },
    });
  }

  initiate2fa() {
    this.mfaPhase = 'PROCESSING';
    this.apiService
      .post<
        ApiResponse<TotpSetupResponse>
      >('users/me/2fa/totp/setup/start', null)
      .subscribe({
        next: (res) => {
          if (!res.success) {
            this.alertifyService.error(
              res.message || 'Failed to initiate totp setup',
            );
            this.mfaPhase = 'DISABLED';
          } else {
            this.alertifyService.success(res.message);
            this.manualEntryKey = res.data.manual_entry_key;
            this.provisioningUri.set(res.data.provisioning_uri);
            this.mfaPhase = 'SETUP_PENDING';
          }
        },
        error: (error) => {
          this.alertifyService.error(
            this.apiService.getErrorMessage(error) ||
              'Failed to initiate totp setup',
          );
          this.mfaPhase = 'DISABLED';
        },
      });
  }

  disable2fa() {
    this.mfaPhase = 'PROCESSING';
    this.apiService
      .post<ApiResponse>('users/me/2fa/remove', null)
      .pipe(
        finalize(() =>
          this.authenticationService.initializeUserData().subscribe(),
        ),
      )
      .subscribe({
        next: (res) => {
          if (!res.success) {
            this.alertifyService.error(res.message || 'Failed to disable totp');
          } else {
            this.alertifyService.success(res.message);
          }
        },
        error: (error) => {
          this.alertifyService.error(
            this.apiService.getErrorMessage(error) || 'Failed to disable totp',
          );
        },
      });
  }

  confirm2faSetup() {
    this.mfaPhase = 'PROCESSING';
    const payload = {
      code: this.totpCode,
    };
    this.apiService
      .post<ApiResponse>('users/me/2fa/totp/setup/confirm', payload)
      .pipe(
        finalize(() =>
          this.authenticationService.initializeUserData().subscribe(),
        ),
      )
      .subscribe({
        next: (res) => {
          if (!res.success) {
            this.alertifyService.error(
              res.message || 'Failed to confirm totp setup',
            );
          } else {
            this.alertifyService.success(res.message);
          }
        },
        error: (error) => {
          this.alertifyService.error(
            this.apiService.getErrorMessage(error) ||
              'Failed to confirm totp setup',
          );
        },
        complete: () => {
          this.authenticationService.initializeUserData();
        },
      });
  }

  toggleTotp() {
    this.isActive.update((val) => {
      if (!val) this.initiate2fa();
      else this.disable2fa();
      return !val;
    });
  }

  async copyManualEntryKey() {
    if (this.isCopied) return;
    try {
      if (!this.manualEntryKey)
        throw new Error('Failed to find manual entry key');

      await navigator.clipboard.writeText(this.manualEntryKey);
      this.alertifyService.success('Copied to clipboard!');
      this.isCopied = true;

      setTimeout(() => {
        this.isCopied = false;
      }, 1500);
    } catch (error: unknown) {
      if (typeof error === 'string') {
        this.alertifyService.error(error);
      }
    }
  }

  onAuthCodeInput(event: Event) {
    const target = event.target as any;
    const { value } = target;

    if (!value) {
      this.isCodeInputValid = false;
      return;
    }

    if ((value as string).length < 6) {
      this.isCodeInputValid = false;
      return;
    }

    if ((value as string).length > 6) {
      target.value = (value as string).slice(0, 6);
    }

    this.totpCode = target.value;
    this.isCodeInputValid = true;
  }

  onAuthCodeKeyDown(event: KeyboardEvent) {
    const validCodes = [
      'Backspace',
      'ArrowRight',
      'ArrowDown',
      'ArrowUp',
      'ArrowLeft',
      'Enter',
    ];
    if (!/^\d$/.test(event.key) && !validCodes.includes(event.code))
      event.preventDefault();
  }

  onClose() {
    this.close.emit(false);
  }
}
